import { ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef, memo } from "react";
import { useMousePosition } from "@/hooks/useMousePosition";
import FloatingIcons from "@/components/FloatingIcons";
import { useTranslation } from "@/contexts/LanguageContext";
import { usePersona } from "@/contexts/PersonaContext";

const MemoizedFloatingIcons = memo(FloatingIcons);

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export default function Hero() {
  const [reduceMotion] = useState(prefersReducedMotion);
  const mouse = useMousePosition();
  const { t } = useTranslation();
  const { persona } = usePersona();

  const positioning = t("hero.positioning") as string;
  const ctaKey = persona !== "general" ? `hero.cta.${persona}` : "hero.cta.general";
  const ctaPrimary = t(ctaKey) as string;
  const ctaSecondary = t("hero.cta.secondary") as string;

  const orb1Ref = useRef<HTMLDivElement>(null);
  const orb2Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reduceMotion) return;
    const px = (mouse.x - window.innerWidth / 2) * 0.02;
    const py = (mouse.y - window.innerHeight / 2) * 0.02;
    if (orb1Ref.current) {
      orb1Ref.current.style.transform = `translate3d(${px * 3}px, ${py * 3}px, 0)`;
    }
    if (orb2Ref.current) {
      orb2Ref.current.style.transform = `translate3d(${-px * 2}px, ${-py * 2}px, 0)`;
    }
  }, [mouse.x, mouse.y, reduceMotion]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-6" aria-label="Hero introduction">
      <div className="absolute inset-0 dot-grid" />

      <div
        ref={orb1Ref}
        className="absolute w-[500px] h-[500px] rounded-full opacity-20 blur-[100px] bg-foreground will-change-transform"
        style={{ top: "10%", right: "10%" }}
      />
      <div
        ref={orb2Ref}
        className="absolute w-[400px] h-[400px] rounded-full opacity-10 blur-[80px] will-change-transform"
        style={{ background: "hsl(var(--foreground))", bottom: "10%", left: "5%" }}
      />

      <MemoizedFloatingIcons />

      <div className="relative z-10 text-center max-w-5xl mx-auto min-h-[420px] sm:min-h-[480px] md:min-h-[560px] flex flex-col justify-center">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95]">
            <span className="text-muted-foreground">{t("hero.build")}</span>
            <br />
            <span className="shimmer font-bold">{t("hero.intelligent")}</span>
            <br />
            <span className="text-muted-foreground">{t("hero.products")}</span>
          </h1>

          <div className="mt-8 max-w-2xl mx-auto px-2">
            <p className="text-base md:text-lg text-muted-foreground font-normal leading-relaxed">
              {positioning}
            </p>
          </div>

          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/contact"
              className="group px-7 py-3.5 bg-foreground/80 text-background rounded-full text-sm font-medium hover:bg-foreground/70 transition-all"
            >
              {ctaPrimary}
              <span className="inline-block ml-1 group-hover:translate-x-1 transition-transform">→</span>
            </Link>
            <Link
              to="/case-studies"
              className="px-7 py-3.5 border border-border rounded-full text-sm font-medium text-foreground hover:border-foreground hover:text-foreground transition-all bg-background/50 backdrop-blur-sm"
            >
              {ctaSecondary}
            </Link>
          </div>
        </div>

        <div className="mt-16 hero-scroll-indicator">
          <ArrowDown size={20} className="text-muted-foreground mx-auto hero-scroll-arrow" />
        </div>
      </div>
    </section>
  );
}
